import PropTypes from 'prop-types';
import React, { Component } from 'react';
import styled from 'styled-components';
import ErrorMessage from '@zocdoc-frontend-common/components-errormessage';
import { FontFamily } from '@zocdoc-frontend-common/styles-typography';
import { filterToValidAttributes } from '@zocdoc-frontend-common/utils-dom-property';
import { navy, grey25, coral } from '@zocdoc/branding/colors';

export class Textbox extends Component {
    static propTypes = {
        className: PropTypes.string,
        focusOn: PropTypes.bool,
        focusOnMount: PropTypes.bool,
        icon: PropTypes.func,
        iconHover: PropTypes.func,
        invalidMessage: PropTypes.node,
        maxLength: PropTypes.number,
        name: PropTypes.string,
        placeholder: PropTypes.string,
        readOnly: PropTypes.bool,
        type: PropTypes.string,
        inputMode: PropTypes.string,
    };

    static defaultProps = {
        iconHover: () => {},
        type: 'text',
        inputMode: 'text',
    };

    componentDidUpdate() {
        if (this.props.focusOn) {
            this.inputRef && this.inputRef.focus();
        }
    }

    componentDidMount() {
        if (this.props.focusOnMount) {
            this.inputRef && this.inputRef.focus();
        }
    }

    render() {
        const {
            className,
            defaultValue,
            icon,
            iconHover,
            invalidMessage,
            onChange,
            noBorder,
            placeholder,
            readOnly,
            autoComplete,
            spellCheck,
            autoCapitalize,
            autoCorrect,
            maxLength,
            inputMode,
            ...otherProps
        } = this.props;

        const attributes = filterToValidAttributes(otherProps);
        const hasIcon = !!icon;
        const dataTest = invalidMessage
            ? 'invalid-textbox-input'
            : 'textbox-input';
        return (
            <TextboxWrapper data-test="textbox-wrapper">
                {this.renderIcon(icon)}
                <Input
                    autoCapitalize={autoCapitalize}
                    autoComplete={autoComplete}
                    autoCorrect={autoCorrect}
                    className={className}
                    data-test={dataTest} // HACK: React 15 + IE11 has bugs with onChange
                    defaultValue={defaultValue}
                    hasIcon={hasIcon}
                    ref={(ref) => {
                        this.inputRef = ref;
                    }}
                    isInvalid={!!invalidMessage}
                    maxLength={maxLength}
                    noBorder={noBorder}
                    onChange={onChange}
                    onInput={onChange}
                    placeholder={placeholder}
                    readOnly={readOnly}
                    spellCheck={spellCheck}
                    inputMode={inputMode}
                    {...attributes}
                />
                {this.props.children}
                {this.renderInvalidErrorMessage(invalidMessage)}
            </TextboxWrapper>
        );
    }

    renderInvalidErrorMessage(message) {
        if (!message) {
            return;
        }

        return <ErrorMessage data-test="error-message">{message}</ErrorMessage>;
    }

    renderIcon(Icon) {
        if (Icon) {
            return (
                <IconWrapper data-test="textbox-icon-wrapper">
                    <Icon
                        onMouseEnter={this.handleMouseEnter}
                        onMouseLeave={this.handleMouseLeave}
                    />
                </IconWrapper>
            );
        }
    }

    handleMouseEnter = () => {
        this.props.iconHover(true);
    };

    handleMouseLeave = () => {
        this.props.iconHover(false);
    };

    blur = () => {
        this.inputRef && this.inputRef.blur();
    };

    focus = () => {
        this.inputRef && this.inputRef.focus();
    };
}

const TextboxWrapper = styled.div`
    position: relative;
`;

const Input = styled.input`
    appearance: none;
    background-color: ${(p) => (p.disabled ? grey25 : '')};
    border-color: ${(p) => (p.isInvalid ? coral : grey25)};
    border-radius: 0;
    border-style: solid;
    border-width: ${(p) => (p.noBorder ? 0 : 1)}px;
    box-sizing: border-box;
    color: ${navy};
    cursor: ${(p) => (p.disabled ? 'not-allowed' : 'pointer')};
    font: 15px/24px ${FontFamily.SharpSansMedium};
    height: 48px;
    padding: 14px 12px 12px ${(p) => (p.hasIcon ? 37 : 12)}px;
    width: 100%;

    &:focus {
        border-color: ${navy};
    }
`;

export const IconWrapper = styled.div`
    width: 25px;
    height: 25px;
    position: absolute;
    left: 12px;
    top: 12px;
`;
